import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { FilterInfo, FilterValue } from 'types/pagination';

/**
 * Safely parse a JSON string. Returns the provided fallback when the input
 * is empty/null/undefined or malformed. URL params are user-controlled and
 * can be corrupted (stale bookmarks, manual edits, format migrations), so a
 * malformed `filters` param should not throw inside render and crash the tree.
 */
function safeJsonParse<T>(raw: string | null | undefined, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

/**
 *
 * @param filterKey - If we need to get only 1 filter and listent to its change
 * @returns array of values for the filter key
 * if no filter key provided, will listed to all filter change and returns all filters
 * Usefull when you don't want to trigger effects on pagination change as it is also in the url
 */
function useUrlFilters<T extends FilterValue = string>(filterKey: string): T[];
// eslint-disable-next-line no-redeclare
function useUrlFilters(): FilterInfo;
// eslint-disable-next-line no-redeclare
function useUrlFilters<T extends FilterValue = string>(
  filterKey?: string,
): T[] | FilterInfo {
  const location = useLocation();
  const rawFilterString = new URLSearchParams(location.search).get('filters');

  // For a keyed read, parse the raw filters object, pull out the key
  // (defaulting to []), and stringify it again so it can serve as a stable
  // memoization key. For an unkeyed read, pass the raw string through and
  // let the memo parse it once.
  const filterString = filterKey
    ? JSON.stringify(
        safeJsonParse<FilterInfo>(rawFilterString, {})?.[filterKey] ?? [],
      )
    : rawFilterString || '{}';

  return useMemo(() => {
    if (filterKey) {
      return safeJsonParse<T[]>(filterString, []);
    }
    return safeJsonParse<FilterInfo>(filterString, {});
  }, [filterString, filterKey]);
}

export default useUrlFilters;
